import {
  CKButton,
  CKLayoutHorizontal,
  CKSearch,
  CKSelectableItem,
} from 'clearkit';
import { FC, useEffect, useState } from 'react';

import { GridList } from '~/components/unified/GridList';
import { SettingsCard } from '~/components/unified/SettingsCard';
import { Membership, UserRoleEnum } from '~/generated/graphql';

import { memberSelectRoles, MembershipSelect } from './MembershipSelect';
import { RemoveMembershipButton } from './RemoveMembershipButton';
import { useProfilePermissions } from './useProfilePermissions';

type TeamMembershipListProps = {
  onPermissionsChange: (id: string, role: UserRoleEnum) => void;
  onRemoveInvite: (id: string) => void;
  onRemoveMember: (id: string) => void;
  memberships: any;
  invites: any;
};

const textOffsetClasses = 'pt-2';
const sectionClasses = 'text-sm grid gap-4';

export const TeamMembershipList: FC<TeamMembershipListProps> = ({
  onPermissionsChange,
  onRemoveInvite,
  onRemoveMember,
  memberships,
  invites,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [filteredMemberships, setFilteredMemberships] = useState(memberships);
  const [filteredInvites, setFilteredInvites] = useState(invites);

  const { isOwner } = useProfilePermissions();

  const removeInvite = async (id: string) => {
    setIsLoading(true);
    await onRemoveInvite(id);
    setIsLoading(false);
  };

  const updateMembership = async (id: string, newValue: CKSelectableItem) => {
    setIsLoading(true);
    await onPermissionsChange(id, newValue.value as UserRoleEnum);
    setIsLoading(false);
  };

  const removeMembership = async (id: string) => {
    setIsLoading(true);
    await onRemoveMember(id);
    setIsLoading(false);
  };

  useEffect(() => {
    const membershipsFiltered = memberships.filter((membership: Membership) => {
      const { user } = membership;
      const email = user.email.toLowerCase();
      const name = (user.name || '').toLowerCase();
      const filterLowerCase = filter.toLowerCase();

      return email.includes(filterLowerCase) || name.includes(filterLowerCase);
    });

    const invitesFiltered = invites.filter((invite: any) => {
      const email = invite.email.toLowerCase();
      const filterLowerCase = filter.toLowerCase();

      return email.includes(filterLowerCase);
    });

    setFilteredMemberships(membershipsFiltered);
    setFilteredInvites(invitesFiltered);
  }, [filter, memberships, invites]);

  return (
    <GridList
      className="space-y-8"
      gridTemplateColumns="repeat(auto-fit, minmax(12rem, 1fr))"
    >
      <SettingsCard>
        <SettingsCard.Header className="flex flex-wrap justify-between min-w-0">
          <SettingsCard.Heading>Team members</SettingsCard.Heading>
          <CKSearch
            className="w-72"
            onChange={setFilter}
            placeholder="Search members by name or email"
            value={filter}
          />
        </SettingsCard.Header>
        <SettingsCard.Body className={sectionClasses}>
          {filteredMemberships.length ? (
            <>
              <GridList.Header>
                <div>Team Member</div>
                <div>Role</div>
                <div></div>
              </GridList.Header>
              {filteredMemberships.map((membership: Membership) => {
                const { user, role } = membership;

                const roleLabel = memberSelectRoles.find(
                  (item) => item.value === role,
                );

                return (
                  <GridList.Item key={user.email}>
                    <div className="overflow-hidden">
                      {user.name ? (
                        <div className="font-medium truncate">{user.name}</div>
                      ) : null}
                      <div className="truncate">{user.email}</div>
                    </div>
                    <MembershipSelect
                      hasBasicPermissions
                      isDisabled={isLoading || !isOwner}
                      onChange={(newValue) =>
                        updateMembership(user.id, newValue)
                      }
                      value={{
                        label: roleLabel?.label || role,
                        value: role,
                      }}
                    />
                    <CKLayoutHorizontal justifyContent="end">
                      <RemoveMembershipButton
                        isDisabled={!isOwner}
                        isLoading={isLoading}
                        onChange={removeMembership}
                        onLoadingChange={setIsLoading}
                        user={user}
                      />
                    </CKLayoutHorizontal>
                  </GridList.Item>
                );
              })}
            </>
          ) : (
            <div className="py-6">
              No members found that match <strong>{filter}</strong>
            </div>
          )}
        </SettingsCard.Body>
      </SettingsCard>

      <SettingsCard>
        <SettingsCard.Header>
          <SettingsCard.Heading>Team invites</SettingsCard.Heading>
        </SettingsCard.Header>
        <SettingsCard.Body className={sectionClasses}>
          {filteredInvites.length ? (
            filteredInvites.map((invite: any) => {
              return (
                <GridList.Item key={invite.email}>
                  <div className={textOffsetClasses}>{invite.email}</div>
                  <div></div>
                  <CKLayoutHorizontal justifyContent="end">
                    <CKButton
                      isLoading={isLoading}
                      onClick={() => removeInvite(invite.id)}
                      variant="simple"
                      variantColor="red"
                    >
                      Cancel invitation
                    </CKButton>
                  </CKLayoutHorizontal>
                </GridList.Item>
              );
            })
          ) : (
            <div className="py-6">
              {filter ? (
                <span>
                  No invites found that match <strong>{filter}</strong>
                </span>
              ) : (
                <span>No active invites.</span>
              )}
            </div>
          )}
        </SettingsCard.Body>
      </SettingsCard>
    </GridList>
  );
};
